import { useMemo, useRef, useState } from "react";
import {
  Alert, Badge, Box, Breadcrumbs, Button, Chip, CircularProgress, FormControlLabel, IconButton, Link, Paper, Stack, Switch,
  Table, TableBody, TableCell, TableHead, TableRow, TableSortLabel, TextField, Tooltip, Typography,
} from "@mui/material";
import ChatBubbleOutlineIcon from "@mui/icons-material/ChatBubbleOutline";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import dayjs from "dayjs";
import { Link as RouterLink, useParams } from "react-router-dom";
import { AppLayout } from "@/components/AppLayout";
import {
  createComment, deleteComment, errorMessage, fetchCheck, reviewCheck, setCommentResolved,
} from "@/api/closeReview";
import type { CheckDetail, CheckRow, ReviewComment, ReviewStatus } from "@/api/closeReview";
import { Cell, formatValue, numeric, ReviewChip, rowLinks, StatusChip } from "./shared";

const MAX_BODY = 2000;
const PANE_KEY = "closeReview.paneOpen";

// The review and comments pane can fold to a thin rail so the rows table gets the full width; the choice is
// remembered per browser.
function usePaneOpen(): [boolean, (open: boolean) => void] {
  const [open, setOpen] = useState(() => {
    try {
      return window.localStorage.getItem(PANE_KEY) !== "false";
    } catch {
      return true;
    }
  });
  const set = (next: boolean) => {
    setOpen(next);
    try {
      window.localStorage.setItem(PANE_KEY, String(next));
    } catch {
      // Storage blocked (private window): the pane still toggles for this visit.
    }
  };
  return [open, set];
}

function ReviewBar({ month, check }: { month: string; check: CheckDetail }) {
  const qc = useQueryClient();
  const [note, setNote] = useState("");
  const review = useMutation({
    mutationFn: (status: ReviewStatus) => reviewCheck(month, check.key, status, note),
    onSuccess: () => { setNote(""); qc.invalidateQueries({ queryKey: ["close-review"] }); },
  });
  const action = (status: ReviewStatus, label: string, needsNote = false) => (
    <Button size="small" variant={check.review_status === status ? "contained" : "outlined"}
      disabled={review.isPending || check.review_status === status || (needsNote && !note.trim())}
      onClick={() => review.mutate(status)}>
      {label}
    </Button>
  );

  return (
    <Paper variant="outlined" sx={{ p: 2 }}>
      <Stack spacing={1}>
        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
          <Typography variant="subtitle2">Review</Typography>
          <ReviewChip status={check.review_status} />
          {check.reviewed_by && (
            <Typography variant="body2" color="text.secondary">
              by {check.reviewed_by} {dayjs(check.reviewed_at).format("MMM D, h:mm A")}
              {check.review_note ? `: ${check.review_note}` : ""}
            </Typography>
          )}
          {check.changed_since_review && <Chip size="small" color="warning" label="Result changed since review" />}
        </Stack>
        <TextField size="small" placeholder="Note (required to waive)" value={note} onChange={(e) => setNote(e.target.value)} />
        <Stack direction="row" spacing={1}>
          {action("acknowledged", "Acknowledge")}
          {action("resolved", "Resolve")}
          {action("waived", "Waive", true)}
          {check.review_status !== "open" && action("open", "Reopen")}
        </Stack>
        {review.isError && <Alert severity="error">{errorMessage(review.error)}</Alert>}
      </Stack>
    </Paper>
  );
}

// How a row reads to a person: its first text column (the customer, vendor, item or entry), with the
// account's name after its number ("12105 Inventory Manual") when the row has both.
function rowLabel(check: CheckDetail, key: string | null): string {
  if (!key) return "";
  const row = check.rows.find((r) => r.key === key);
  const col = check.columns.find((c) => c.type === "text");
  const value = row && col ? row.values[col.key] : null;
  if (!value) return key;
  const name = col?.key === "account" && check.columns.some((c) => c.key === "name") ? row?.values.name : null;
  return name ? `${value} ${name}` : String(value);
}

function Comments({ month, check, row, onClearRow }: { month: string; check: CheckDetail; row: CheckRow | null; onClearRow: () => void }) {
  const qc = useQueryClient();
  const [body, setBody] = useState("");
  const refresh = () => qc.invalidateQueries({ queryKey: ["close-review"] });
  const create = useMutation({
    mutationFn: () => createComment(month, check.key, body, row?.id),
    onSuccess: () => { setBody(""); refresh(); },
  });
  const resolve = useMutation({ mutationFn: (c: ReviewComment) => setCommentResolved(c.id, !c.resolved), onSuccess: refresh });
  const remove = useMutation({ mutationFn: (c: ReviewComment) => deleteComment(c.id), onSuccess: refresh });
  const shown = row ? check.comments.filter((c) => c.row_id === row.id) : check.comments;

  return (
    <Paper variant="outlined" sx={{ p: 2 }}>
      <Stack spacing={1.5}>
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Typography variant="subtitle2">{row ? `Comments on ${rowLabel(check, row.key)}` : "Comments"}</Typography>
          {row && <Button size="small" onClick={onClearRow}>All comments</Button>}
        </Stack>
        <Stack spacing={1} component="form" onSubmit={(e) => { e.preventDefault(); create.mutate(); }}>
          <TextField multiline minRows={2} size="small" value={body} onChange={(e) => setBody(e.target.value)}
            placeholder={row ? "Comment on this row" : "Comment on this check"} error={body.length > MAX_BODY}
            helperText={`${body.length}/${MAX_BODY}`} />
          {create.isError && <Alert severity="error">{errorMessage(create.error)}</Alert>}
          <Box>
            <Button type="submit" size="small" variant="contained" disabled={!body.trim() || body.length > MAX_BODY || create.isPending}>
              Post
            </Button>
          </Box>
        </Stack>
        {shown.length === 0 && <Typography variant="body2" color="text.secondary">No comments yet.</Typography>}
        {shown.map((c) => (
          <Box key={c.id} sx={{ borderLeft: 3, borderColor: c.resolved ? "success.light" : "primary.light", pl: 1.5, opacity: c.resolved ? 0.7 : 1 }}>
            <Typography variant="caption" color="text.secondary">
              {c.author} · {dayjs(c.created_at).format("MMM D, h:mm A")}{c.row_key && !row ? ` · ${rowLabel(check, c.row_key)}` : ""}
              {c.resolved ? ` · resolved by ${c.resolved_by}` : ""}
            </Typography>
            <Typography variant="body2" sx={{ whiteSpace: "pre-wrap" }}>{c.body}</Typography>
            <Stack direction="row" spacing={1}>
              <Button size="small" onClick={() => resolve.mutate(c)}>{c.resolved ? "Reopen" : "Resolve"}</Button>
              {c.can_delete && <Button size="small" color="error" onClick={() => remove.mutate(c)}>Delete</Button>}
            </Stack>
          </Box>
        ))}
      </Stack>
    </Paper>
  );
}

function RowsTable({ check, selected, onSelect }: { check: CheckDetail; selected: CheckRow | null; onSelect: (r: CheckRow) => void }) {
  const [search, setSearch] = useState("");
  const [hideCleared, setHideCleared] = useState(true);
  const [sort, setSort] = useState<{ key: string; dir: "asc" | "desc" } | null>(null);
  const rows = useMemo(() => {
    const term = search.trim().toLowerCase();
    let out = check.rows.filter((r) => !(hideCleared && r.cleared));
    if (term) out = out.filter((r) => Object.values(r.values).some((v) => String(v ?? "").toLowerCase().includes(term)));
    if (sort) {
      out = [...out].sort((a, b) => {
        const x = a.values[sort.key], y = b.values[sort.key];
        const cmp = typeof x === "number" && typeof y === "number" ? x - y : String(x ?? "").localeCompare(String(y ?? ""));
        return sort.dir === "asc" ? cmp : -cmp;
      });
    }
    return out;
  }, [check.rows, search, hideCleared, sort]);

  if (check.rows.length === 0) return null;
  const anyCleared = check.rows.some((r) => r.cleared);
  return (
    <Paper variant="outlined">
      <Stack direction="row" spacing={2} sx={{ p: 1.5 }} alignItems="center" flexWrap="wrap" useFlexGap>
        <TextField size="small" placeholder="Search rows" value={search} onChange={(e) => setSearch(e.target.value)} />
        {anyCleared && (
          <FormControlLabel control={<Switch size="small" checked={hideCleared} onChange={(e) => setHideCleared(e.target.checked)} />}
            label="Hide cleared" />
        )}
        <Typography variant="body2" color="text.secondary">
          {rows.length === check.rows.length ? `${rows.length} rows` : `${rows.length} of ${check.rows.length} rows`} · click a row to comment on it
        </Typography>
      </Stack>
      <Box sx={{ overflow: "auto", maxHeight: "calc(100vh - 300px)" }}>
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              {check.columns.map((c) => (
                <TableCell key={c.key} align={numeric(c.type) ? "right" : "left"} sortDirection={sort?.key === c.key ? sort.dir : false}
                  sx={{ whiteSpace: "nowrap", fontWeight: 600 }}>
                  <TableSortLabel active={sort?.key === c.key} direction={sort?.key === c.key ? sort.dir : "asc"}
                    onClick={() => setSort({ key: c.key, dir: sort?.key === c.key && sort.dir === "desc" ? "asc" : "desc" })}>
                    {c.label}
                  </TableSortLabel>
                </TableCell>
              ))}
              <TableCell />
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((r) => {
              const links = rowLinks(r.values);
              return (
                <TableRow key={r.id} hover selected={selected?.id === r.id} tabIndex={0} onClick={() => onSelect(r)}
                  onKeyDown={(e) => { if (e.key === "Enter") onSelect(r); }}
                  sx={{ opacity: r.cleared ? 0.5 : 1, cursor: "pointer" }}>
                  {check.columns.map((c) => (
                    <TableCell key={c.key} align={numeric(c.type) ? "right" : "left"}
                      sx={{ whiteSpace: "nowrap", fontVariantNumeric: "tabular-nums" }}>
                      <Cell value={r.values[c.key]} type={c.type} href={links[c.key]} />
                    </TableCell>
                  ))}
                  <TableCell align="right" sx={{ width: 48 }}>
                    <Tooltip title={r.cleared ? "Cleared from the latest run" : r.comment_count ? `${r.comment_count} comment(s)` : "Comment on this row"}>
                      <Badge badgeContent={r.comment_count} color="primary">
                        <ChatBubbleOutlineIcon fontSize="small" sx={{ color: r.comment_count ? "primary.main" : "action.disabled" }} />
                      </Badge>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </Box>
    </Paper>
  );
}

export function CloseReviewCheckPage() {
  const { month = "", key = "" } = useParams();
  const [row, setRow] = useState<CheckRow | null>(null);
  const [paneOpen, setPaneOpen] = usePaneOpen();
  const commentsRef = useRef<HTMLDivElement>(null);
  const selectRow = (r: CheckRow) => {
    setRow(r);
    setPaneOpen(true); // picking a row is how you comment on it, so bring the pane back
    commentsRef.current?.scrollIntoView({ behavior: "smooth", block: "nearest" });
  };
  const page = useQuery({ queryKey: ["close-review", "check", month, key], queryFn: () => fetchCheck(month, key), retry: false });
  const check = page.data?.check;

  return (
    <AppLayout maxWidth="xl">
      <Stack spacing={2}>
        <Breadcrumbs>
          <Link component={RouterLink} to="/close-review">Close Review</Link>
          <Link component={RouterLink} to={`/close-review/${month}`}>{page.data?.period.label ?? month}</Link>
          <Typography color="text.primary">{check?.title ?? key}</Typography>
        </Breadcrumbs>
        {page.isLoading && <CircularProgress />}
        {page.isError && <Alert severity="error">{errorMessage(page.error)}</Alert>}
        {check && (
          <Box sx={{ display: "grid", gridTemplateColumns: { xs: "minmax(0, 1fr)", lg: `minmax(0, 1fr) ${paneOpen ? "340px" : "44px"}` }, gap: 2, alignItems: "start" }}>
            <Stack spacing={2} sx={{ minWidth: 0 }}>
              <Paper variant="outlined" sx={{ p: 2 }}>
                <Stack spacing={1}>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Typography variant="h6">{check.title}</Typography>
                    <StatusChip status={check.status} />
                  </Stack>
                  <Typography variant="body2">{check.summary}</Typography>
                  {check.expected !== null && check.actual !== null && (
                    <Stack direction="row" spacing={4} sx={{ pt: 0.5 }}>
                      {[["Expected (GL)", check.expected], ["Actual", check.actual], ["Difference", check.difference]].map(([label, v]) => (
                        <Box key={String(label)}>
                          <Typography variant="caption" color="text.secondary">{label}</Typography>
                          <Typography variant="subtitle1" sx={{ fontVariantNumeric: "tabular-nums" }}>{formatValue(v, "money")}</Typography>
                        </Box>
                      ))}
                    </Stack>
                  )}
                  <Typography variant="caption" color="text.secondary">
                    {check.basis === "run_date" ? "As of the run date" : "As of the month end"}
                    {check.account ? ` · account ${check.account}` : ""}
                    {` · updated ${dayjs(check.last_run_at).format("MMM D, h:mm A")}`}
                  </Typography>
                </Stack>
              </Paper>
              <RowsTable check={check} selected={row} onSelect={selectRow} />
              {check.history.length > 1 && (
                <Paper variant="outlined">
                  <Typography variant="subtitle2" sx={{ px: 2, pt: 1.5 }}>History</Typography>
                  <Table size="small">
                    <TableHead>
                      <TableRow><TableCell>Run</TableCell><TableCell>Result</TableCell><TableCell align="right">Difference</TableCell></TableRow>
                    </TableHead>
                    <TableBody>
                      {check.history.map((h) => (
                        <TableRow key={h.run_at}>
                          <TableCell>{dayjs(h.run_at).format("MMM D, h:mm A")}{h.business_day ? ` (day ${h.business_day})` : ""}</TableCell>
                          <TableCell><StatusChip status={h.status} /></TableCell>
                          <TableCell align="right">{formatValue(h.difference, "money")}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </Paper>
              )}
            </Stack>
            {paneOpen ? (
              <Stack spacing={1}>
                <Stack direction="row" justifyContent="flex-end">
                  <Button size="small" color="inherit" endIcon={<ChevronRightIcon />} onClick={() => setPaneOpen(false)}>
                    Hide review and comments
                  </Button>
                </Stack>
                <ReviewBar month={month} check={check} />
                <Box ref={commentsRef}>
                  <Comments month={month} check={check} row={row} onClearRow={() => setRow(null)} />
                </Box>
              </Stack>
            ) : (
              <Paper variant="outlined" sx={{ py: 1, display: "flex", flexDirection: { xs: "row", lg: "column" }, alignItems: "center", justifyContent: "center", gap: 1 }}>
                <Tooltip title="Show review and comments" placement="left">
                  <IconButton size="small" onClick={() => setPaneOpen(true)} aria-label="Show review and comments">
                    <ChevronLeftIcon />
                  </IconButton>
                </Tooltip>
                <Tooltip title={`${check.comments.length} comment${check.comments.length === 1 ? "" : "s"}`} placement="left">
                  <IconButton size="small" onClick={() => setPaneOpen(true)} aria-label="Show comments">
                    <Badge badgeContent={check.comments.length} color="primary" max={99}>
                      <ChatBubbleOutlineIcon fontSize="small" />
                    </Badge>
                  </IconButton>
                </Tooltip>
              </Paper>
            )}
          </Box>
        )}
      </Stack>
    </AppLayout>
  );
}
